import { NotificationEventType, NotificationPayload } from '../models/notification.models';

/** Trailing batching window. First notification immediately, then one grouped per window. */
export const DESKTOP_NOTIFICATION_BATCH_WINDOW_MS = 10_000;

export interface BatchEntry {
  eventType: NotificationEventType;
  payload: NotificationPayload;
}

/** A claim outlives the batch window by a wide margin, so a slow tab cannot re-emit a stale event. */
export const DESKTOP_SEEN_TTL_MS = 300_000;

/** Hard cap on the ledger, so a browser left open for weeks cannot grow the record without bound. */
export const DESKTOP_SEEN_MAX = 100;

export interface SeenClaim {
  /** The notification_id the SSE event carried. */
  id: number;
  owner: string;
  ts: number;
}

export interface BatchState {
  windowClosesAt: number | null;
  pending: BatchEntry[];
  seen: SeenClaim[];
}

export const EMPTY_BATCH_STATE: BatchState = { windowClosesAt: null, pending: [], seen: [] };

export type BatchEmission =
  | { kind: 'single'; entry: BatchEntry }
  /** `eventType` is null when the window held more than one event type. */
  | { kind: 'grouped'; count: number; eventType: NotificationEventType | null };

export interface BatchDecision {
  state: BatchState;
  emission: BatchEmission | null;
}

function distinctEventType(pending: BatchEntry[]): NotificationEventType | null {
  const first = pending[0].eventType;
  return pending.every((entry) => entry.eventType === first) ? first : null;
}

export function isClaimed(state: BatchState, notificationId: number): boolean {
  return state.seen.some((claim) => claim.id === notificationId);
}

/**
 * Records a claim on one notification id. Expire first, then cap: the ledger is
 * append-ordered, so its newest entries are its tail. Expiry is evaluated here rather
 * than on a timer.
 */
export function recordClaim(
  state: BatchState,
  now: number,
  notificationId: number,
  owner: string,
): BatchState {
  const live = state.seen.filter((claim) => now - claim.ts < DESKTOP_SEEN_TTL_MS);
  const appended = [...live, { id: notificationId, owner, ts: now }];
  return { ...state, seen: appended.slice(-DESKTOP_SEEN_MAX) };
}

export function flush(state: BatchState, now: number): BatchDecision {
  if (state.windowClosesAt === null) {
    return { state, emission: null };
  }
  if (state.pending.length === 0) {
    return { state: { ...state, windowClosesAt: null, pending: [] }, emission: null };
  }

  const reopened: BatchState = {
    ...state,
    windowClosesAt: now + DESKTOP_NOTIFICATION_BATCH_WINDOW_MS,
    pending: [],
  };

  if (state.pending.length === 1) {
    return { state: reopened, emission: { kind: 'single', entry: state.pending[0] } };
  }
  return {
    state: reopened,
    emission: {
      kind: 'grouped',
      count: state.pending.length,
      eventType: distinctEventType(state.pending),
    },
  };
}

/**
 * The window is re-checked on every arrival rather than by timer alone: a backgrounded
 * tab's timers are throttled to roughly one wake per minute, while SSE message delivery
 * is not.
 */
export function admit(state: BatchState, now: number, entry: BatchEntry): BatchDecision {
  let current = state;

  if (current.windowClosesAt !== null && now >= current.windowClosesAt) {
    const flushed = flush(current, now);
    if (flushed.emission) {
      return { state: { ...flushed.state, pending: [entry] }, emission: flushed.emission };
    }
    current = flushed.state;
  }

  if (current.windowClosesAt === null) {
    return {
      state: { ...current, windowClosesAt: now + DESKTOP_NOTIFICATION_BATCH_WINDOW_MS, pending: [] },
      emission: { kind: 'single', entry },
    };
  }

  return { state: { ...current, pending: [...current.pending, entry] }, emission: null };
}
